import { useState, useRef, useEffect } from 'react';
import { useMessages } from '../../hooks/useMessages';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Spinner } from '../ui';
import Avatar from '../shared/Avatar';

/**
 * MessageThread Component
 *
 * Real-time messaging between winner and seller after draw completion.
 * Used to coordinate pickup details.
 */
export default function MessageThread({
  raffleId,
  otherUserId,
  otherUserName,
  otherUserAvatar,
}) {
  const { user, userProfile } = useAuth();
  const { messages, loading, sending, error, send } = useMessages(raffleId, otherUserId);
  const [inputText, setInputText] = useState('');
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);


  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    if (!inputText.trim() || sending) {
      return;
    }

    const success = await send(inputText);
    if (success) {
      setInputText('');
      inputRef.current?.focus();
    }
  }

  function formatTime(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-IE', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div className="bg-midnight-light border border-midnight-lighter rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-midnight-lighter flex items-center gap-3">
        <Avatar
          src={otherUserAvatar}
          name={otherUserName}
          size="sm"
        />
        <div>
          <h3 className="font-semibold text-cream">{otherUserName}</h3>
          <p className="text-xs text-cream/50">Coordinate pickup details</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="h-64 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-cream/50 text-sm">
              No messages yet. Start a conversation to arrange pickup!
            </p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === user?.uid;
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isOwn
                      ? 'bg-primary text-white rounded-br-md'
                      : 'bg-midnight-lighter text-cream rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.text}
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      isOwn ? 'text-white/60' : 'text-cream/40'
                    }`}
                  >
                    {formatTime(message.createdAt)}
                    {isOwn && message.readAt && (
                      <span className="ml-2">Read</span>
                    )}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 pb-2">
          <p className="text-danger text-sm">{error}</p>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-midnight-lighter">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-midnight border border-midnight-lighter rounded-lg px-4 py-2 text-cream placeholder-cream/40 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            maxLength={500}
            disabled={sending}
          />
          <Button
            type="submit"
            disabled={!inputText.trim() || sending}
            loading={sending}
          >
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Compact message preview for showing in cards/lists
 */
export function MessagePreview({ lastMessage, unreadCount }) {
  if (!lastMessage) {
    return (
      <p className="text-cream/50 text-sm">No messages yet</p>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <p className="text-cream/70 text-sm truncate flex-1">
        {lastMessage.text}
      </p>
      {unreadCount > 0 && (
        <span className="bg-primary text-white text-xs font-semibold rounded-full px-2 py-0.5">
          {unreadCount}
        </span>
      )}
    </div>
  );
}
